import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  MenuItem,
  Divider,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { ExpandMore, Article, Refresh } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import { toast } from "react-toastify";
import CustomTextField from "../../../components/common/Textfield";
import {
  fetchSEOConfigs,
  SEOConfigApiItem,
  updateSEOConfig,
  createSEOConfig,
} from "./services/seoConfig";

// Page types definition
interface PageType {
  key: string;
  name: string;
  description: string;
  urlPath: string;
}

const PAGE_TYPES: PageType[] = [
  {
    key: "home",
    name: "Home Page",
    description: "Main landing page of the website",
    urlPath: "/",
  },
  {
    key: "product",
    name: "Product Page",
    description: "Individual product detail pages",
    urlPath: "/products/{slug}",
  },
  {
    key: "category",
    name: "Category Page",
    description: "Product category or collection pages",
    urlPath: "/category/{slug}",
  },
  {
    key: "blog_post",
    name: "Blog Post",
    description: "Individual blog post pages",
    urlPath: "/blog/{slug}",
  },
  {
    key: "blog_list",
    name: "Blog List",
    description: "Main blog listing page",
    urlPath: "/blog",
  },
  {
    key: "about",
    name: "About Page",
    description: "About us or company information pages",
    urlPath: "/about",
  },
  {
    key: "contact",
    name: "Contact Page",
    description: "Contact or support pages",
    urlPath: "/contact",
  },
  {
    key: "funeral_stream",
    name: "Funeral Stream",
    description: "Live streaming pages for funeral services",
    urlPath: "/stream/{slug}",
  },
  {
    key: "memorial",
    name: "Memorial Page",
    description: "Memorial tribute pages for the deceased",
    urlPath: "/memorial/{slug}",
  },
  {
    key: "chema",
    name: "Chema - Fundraising Page",
    description: "Fundraising campaign pages for funeral expenses",
    urlPath: "/chema/{slug}",
  },
];

// Form data per page type
interface SEOFormFields {
  metaTitle: string;
  metaKeywords: string;
  metaDescription: string;
  ogTitle: string;
  ogType: string;
  ogDescription: string;
  ogImageUrl: string;
  twitterCardType: string;
  twitterSite: string;
  twitterCreator: string;
  canonicalUrl: string;
  robots: string;
  existingId?: number;
  isSaving?: boolean;
}

const defaultFormFields = (): SEOFormFields => ({
  metaTitle: "",
  metaKeywords: "",
  metaDescription: "",
  ogTitle: "",
  ogType: "website",
  ogDescription: "",
  ogImageUrl: "",
  twitterCardType: "summary_large_image",
  twitterSite: "",
  twitterCreator: "",
  canonicalUrl: "",
  robots: "index,follow",
  existingId: undefined,
  isSaving: false,
});

const SEOManagement: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [formDataMap, setFormDataMap] = useState<Record<string, SEOFormFields>>(
    () => {
      const initial: Record<string, SEOFormFields> = {};
      PAGE_TYPES.forEach((pt) => {
        initial[pt.key] = defaultFormFields();
      });
      return initial;
    },
  );

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await fetchSEOConfigs();
      const configs: SEOConfigApiItem[] = response.data.results || [];

      const updatedMap: Record<string, SEOFormFields> = {};
      PAGE_TYPES.forEach((pt) => {
        // Match by page_name key (backend stores "home", "product", etc.)
        const existing = configs.find(
          (c) => c.page_name.toLowerCase() === pt.key.toLowerCase(),
        );

        if (existing) {
          updatedMap[pt.key] = {
            metaTitle: existing.meta_title_template || "",
            metaKeywords: existing.meta_keywords || "",
            metaDescription: existing.meta_description_template || "",
            ogTitle: existing.og_title_template || "",
            ogType: existing.og_type || "website",
            ogDescription: existing.og_description_template || "",
            ogImageUrl: existing.og_image_url || "",
            twitterCardType:
              existing.twitter_card_type || "summary_large_image",
            twitterSite: existing.twitter_site || "",
            twitterCreator: existing.twitter_creator || "",
            canonicalUrl: existing.canonical_url_pattern || "",
            robots: existing.robots_meta || "index,follow",
            existingId: existing.id,
            isSaving: false,
          };
        } else {
          updatedMap[pt.key] = defaultFormFields();
        }
      });

      setFormDataMap(updatedMap);
      if (isRefresh) toast.success("Configurations refreshed");
    } catch (error) {
      console.error("Failed to load SEO configs:", error);
      toast.error("Failed to load SEO configurations");
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const handleFieldChange = (
    pageKey: string,
    field: keyof SEOFormFields,
    value: string,
  ) => {
    setFormDataMap((prev) => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        [field]: value,
      },
    }));
  };

  const handleSave = async (pageKey: string) => {
    const pageType = PAGE_TYPES.find((pt) => pt.key === pageKey);
    if (!pageType) return;

    const formData = formDataMap[pageKey];

    setFormDataMap((prev) => ({
      ...prev,
      [pageKey]: { ...prev[pageKey], isSaving: true },
    }));

    try {
      const dataToSend = {
        page_name: pageType.key,
        meta_title_template: formData.metaTitle,
        meta_keywords: formData.metaKeywords,
        meta_description_template: formData.metaDescription,
        og_title_template: formData.ogTitle || null,
        og_type: formData.ogType,
        og_description_template: formData.ogDescription || null,
        og_image_url: formData.ogImageUrl || null,
        twitter_card_type: formData.twitterCardType,
        twitter_site: formData.twitterSite || null,
        twitter_creator: formData.twitterCreator || null,
        canonical_url_pattern: formData.canonicalUrl || null,
        robots_meta: formData.robots,
        is_active: true,
      };

      if (formData.existingId) {
        await updateSEOConfig(formData.existingId, dataToSend);
        toast.success(`${pageType.name} SEO updated successfully`);
      } else {
        const response = await createSEOConfig(dataToSend);

        setFormDataMap((prev) => ({
          ...prev,
          [pageKey]: {
            ...prev[pageKey],
            existingId: response.data.id,
            isSaving: false,
          },
        }));
        toast.success(`${pageType.name} SEO created successfully`);
        return;
      }
    } catch (error) {
      console.error("Failed to save SEO config:", error);
      toast.error(`Failed to save ${pageType.name} SEO configuration`);
    } finally {
      setFormDataMap((prev) => ({
        ...prev,
        [pageKey]: { ...prev[pageKey], isSaving: false },
      }));
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: colors.primary[400],
          borderRadius: "12px 12px 0 0",
          p: { xs: 3, md: 4 },
          mb: 0,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Article sx={{ fontSize: 40, color: colors.primary[900] }} />
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: colors.primary[900],
              fontSize: { xs: "1.5rem", md: "1.8rem" },
            }}
          >
            SEO Management
          </Typography>
          <Typography variant="h5" sx={{ color: `${colors.primary[900]}CC` }}>
            Configure meta tags and SEO settings for different page types
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={1}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0 0 12px 12px",
          mb: 3,
          overflow: "hidden",
          px: 3,
          pt: 2,
          pb: 4,
        }}
      >
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.primary[400] }} />
          </Box>
        )}

        {/* Accordions */}
        {!loading && (
          <Box sx={{ display: "flex", flexDirection: "column", p: 2, gap: 2 }}>
            {PAGE_TYPES.map((pageType) => {
              const formData = formDataMap[pageType.key];
              const isExpanded = expandedPanel === pageType.key;

              return (
                <Accordion
                  key={pageType.key}
                  expanded={isExpanded}
                  onChange={handleAccordionChange(pageType.key)}
                  elevation={0}
                  sx={{
                    borderRadius: "12px !important",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* Accordion Header */}
                  <AccordionSummary
                    expandIcon={
                      <ExpandMore sx={{ color: colors.primary[300] }} />
                    }
                    sx={{
                      bgcolor: isExpanded
                        ? `${colors.primary[400]}10`
                        : "#f3f4f6",
                      borderBottom: isExpanded
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                      px: 3,
                      py: 1.5,
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      },
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Article
                      sx={{
                        fontSize: 28,
                        color: isExpanded
                          ? colors.primary[400]
                          : colors.primary[300],
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: colors.primary[700],
                          fontSize: "1.1rem",
                        }}
                      >
                        {pageType.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: colors.primary[100] }}
                      >
                        {pageType.description}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  {/* Accordion Content */}
                  <AccordionDetails sx={{ p: 3, bgcolor: "background.paper" }}>
                    <Grid container spacing={3}>
                      {/* Meta Title */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Meta Title Template
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="Home Page | Your Site Name"
                          value={formData.metaTitle}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "metaTitle",
                              e.target.value,
                            )
                          }
                          helperText="Use placeholders like {title}, {category}"
                        />
                      </Grid>

                      {/* Meta Keywords */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Meta Keywords
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="keyword1, keyword2, keyword3"
                          value={formData.metaKeywords}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "metaKeywords",
                              e.target.value,
                            )
                          }
                          helperText="Comma-separated keywords"
                        />
                      </Grid>

                      {/* Meta Description */}
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Meta Description Template
                        </Typography>
                        <CustomTextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder={`Default description for ${pageType.name}`}
                          value={formData.metaDescription}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "metaDescription",
                              e.target.value,
                            )
                          }
                          helperText="Recommended: 150-160 characters"
                        />
                      </Grid>

                      {/* Open Graph Settings */}
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: colors.primary[400],
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          Open Graph Settings
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          OG Title Template
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="Social media title"
                          value={formData.ogTitle}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "ogTitle",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          OG Type
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          select
                          value={formData.ogType}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "ogType",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="website">Website</MenuItem>
                          <MenuItem value="article">Article</MenuItem>
                          <MenuItem value="product">Product</MenuItem>
                          <MenuItem value="profile">Profile</MenuItem>
                        </CustomTextField>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          OG Description Template
                        </Typography>
                        <CustomTextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Social media description"
                          value={formData.ogDescription}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "ogDescription",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          OG Image URL
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="https://example.com/image.jpg"
                          value={formData.ogImageUrl}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "ogImageUrl",
                              e.target.value,
                            )
                          }
                          helperText="Recommended: 1200x630px"
                        />
                      </Grid>

                      {/* Twitter Card Settings */}
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: colors.primary[400],
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          Twitter Card Settings
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Card Type
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          select
                          value={formData.twitterCardType}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "twitterCardType",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="summary">Summary</MenuItem>
                          <MenuItem value="summary_large_image">
                            Summary Large Image
                          </MenuItem>
                          <MenuItem value="app">App</MenuItem>
                          <MenuItem value="player">Player</MenuItem>
                        </CustomTextField>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Twitter Site
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="@yourbrand"
                          value={formData.twitterSite}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "twitterSite",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Twitter Creator
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="@author"
                          value={formData.twitterCreator}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "twitterCreator",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      {/* Advanced Settings */}
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: colors.primary[400],
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          Advanced Settings
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Canonical URL Pattern
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          placeholder="https://example.com/{slug}"
                          value={formData.canonicalUrl}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "canonicalUrl",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary[300],
                            mb: 1,
                            display: "block",
                          }}
                        >
                          Robots Meta
                        </Typography>
                        <CustomTextField
                          fullWidth
                          size="small"
                          select
                          value={formData.robots}
                          onChange={(e) =>
                            handleFieldChange(
                              pageType.key,
                              "robots",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="index,follow">
                            index, follow
                          </MenuItem>
                          <MenuItem value="noindex,follow">
                            noindex, follow
                          </MenuItem>
                          <MenuItem value="index,nofollow">
                            index, nofollow
                          </MenuItem>
                          <MenuItem value="noindex,nofollow">
                            noindex, nofollow
                          </MenuItem>
                        </CustomTextField>
                      </Grid>

                      {/* Save Button */}
                      <Grid size={{ xs: 12 }}>
                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => handleSave(pageType.key)}
                            disabled={formData.isSaving}
                            sx={{
                              bgcolor: colors.primary[400],
                              color: colors.primary[900],
                              px: 4,
                              py: 1,
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: 2,
                              "&:hover": { bgcolor: colors.primary[500] },
                            }}
                          >
                            {formData.isSaving ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : formData.existingId ? (
                              "Update Configuration"
                            ) : (
                              "Save Configuration"
                            )}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Refresh Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={
                  refreshing ? <CircularProgress size={16} /> : <Refresh />
                }
                onClick={() => loadConfigs(true)}
                disabled={refreshing}
                sx={{
                  borderColor: colors.primary[300],
                  color: colors.primary[300],
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: colors.primary[400],
                    color: colors.primary[400],
                    bgcolor: `${colors.primary[400]}10`,
                  },
                }}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Template Variables Box */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 3,
          border: `1px solid ${colors.primary[400]}40`,
          bgcolor: `${colors.primary[400]}08`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: colors.primary[400],
            mb: 2,
            fontSize: "0.925rem",
          }}
        >
          Template Variables
        </Typography>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: colors.primary[400],
                display: "block",
                mb: 1,
              }}
            >
              Common placeholders:
            </Typography>
            {[
              { var: "{title}", desc: "Page title" },
              { var: "{description}", desc: "Page description" },
              { var: "{category}", desc: "Category name" },
            ].map((item) => (
              <Box key={item.var} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.primary[500],
                    fontFamily: "monospace",
                    fontWeight: 700,
                  }}
                >
                  {item.var}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.neutral[300] }}>
                  - {item.desc}
                </Typography>
              </Box>
            ))}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: colors.primary[400],
                display: "block",
                mb: 1,
              }}
            >
              Product placeholders:
            </Typography>
            {[
              { var: "{price}", desc: "Product price" },
              { var: "{brand}", desc: "Brand name" },
              { var: "{sku}", desc: "Product SKU" },
            ].map((item) => (
              <Box key={item.var} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.neutral[300],
                    fontFamily: "monospace",
                    fontWeight: 700,
                  }}
                >
                  {item.var}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.neutral[300] }}>
                  - {item.desc}
                </Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SEOManagement;
